import React, { useRef, useEffect } from 'react';
import { Email } from '../useData';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import EmailListCard from './EmailListCard';
import { emailDisplayDateForSort, type EmailListSortBy } from '../utils/emailListSortStorage';

interface EmailCardViewProps {
  emails: Email[];
  onEmailClick: (email: Email) => void;
  onToggleRead?: (email: Email) => void;
  isLoading?: boolean;
  selectedEmailId?: number | null;
  bulkSelectedIds?: Set<number>;
  onBulkToggleSelect?: (id: number, add: boolean) => void;
  /** When the list mixes mailboxes, show account / folder on each card */
  showMailboxLocation?: boolean;
  /** Optional second line when the list is empty */
  emptyStateHint?: string;
  /** Show compact "Read by N" for shared mail */
  showReadByCount?: boolean;
  listSortBy?: EmailListSortBy;
}

const EmailCardView: React.FC<EmailCardViewProps> = ({
  emails,
  onEmailClick,
  onToggleRead,
  isLoading,
  selectedEmailId,
  bulkSelectedIds = new Set(),
  onBulkToggleSelect,
  showMailboxLocation = false,
  emptyStateHint,
  showReadByCount = false,
  listSortBy = 'date',
}) => {
  const { colors } = useTheme();
  const selectedCardRef = useRef<HTMLDivElement | null>(null);
  const selectedOnList = selectedEmailId != null && emails.some((email) => email.id === selectedEmailId);

  useEffect(() => {
    if (!selectedOnList || !selectedCardRef.current) return;
    const frame = requestAnimationFrame(() => {
      selectedCardRef.current?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedEmailId, selectedOnList, emails]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className={colors.textMuted}>Loading emails...</div>
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className={cn('text-center max-w-md px-4', colors.textMuted)}>
          <p className="text-lg mb-2">No emails found</p>
          <p className="text-sm">
            {emptyStateHint ?? 'Emails will appear here once synced from your email accounts.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-0.5 p-1.5">
      {emails.map((email) => (
        <EmailListCard
          key={email.id}
          email={email}
          colors={colors}
          isSelected={selectedEmailId === email.id}
          isBulkSelected={bulkSelectedIds.has(email.id)}
          showMailboxLocation={showMailboxLocation}
          showReadByCount={showReadByCount}
          listSortBy={listSortBy}
          cardRef={selectedEmailId === email.id ? selectedCardRef : undefined}
          onEmailClick={onEmailClick}
          onToggleRead={onToggleRead}
          onBulkToggleSelect={onBulkToggleSelect}
        />
      ))}
    </div>
  );
};

export default EmailCardView;
